/*
  Peter Suchy Jewelers — motion.

  The theme's interaction studies (see the motion-studies page): sections that
  open like a jewelry box as they scroll in, a jeweler's loupe over product
  photos, the Item of the Week turning toward the cursor, accordions that
  glide, and the diamond mark flying into the bag on add to cart.

  Everything here is decoration on top of working markup: with reduced motion
  or without this file, every element is simply shown in its resting state.
*/
(() => {
  'use strict';

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const loupeMedia = matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');

  /* ---------- Jewelry-box reveal on scroll ---------- */

  // [selector, kind]. Only elements below the fold at load are animated, so
  // nothing the visitor sees first ever waits, and the LCP image is untouched.
  const REVEAL = [
    [
      '.banner-pair__media, .cat-split__media, .item-week__media, .about-statement__media, ' +
        '.about-hero__media, .why-hero__media, .era-hero__media, .donate-hero__media',
      'lid',
    ],
    ['main .section-heading, main h2.caps-heading', 'unveil'],
    ['main .product-grid > li', 'rise'],
  ];

  let revealObserver = null;

  const initReveal = (root = document) => {
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
    revealObserver ||= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );

    REVEAL.forEach(([selector, kind]) => {
      root.querySelectorAll(selector).forEach((el) => {
        if (el.dataset.reveal) return;
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight || rect.height === 0) return;
        el.dataset.reveal = kind;
        if (kind === 'rise') {
          const index = Array.prototype.indexOf.call(el.parentElement.children, el);
          el.style.setProperty('--reveal-delay', `${(index % 4) * 90}ms`);
        }
        revealObserver.observe(el);
      });
    });
  };

  /* ---------- Jeweler's loupe (product gallery) ---------- */

  const LOUPE_ZOOM = 2.4;

  const contentBox = (img) => {
    // Where the picture actually sits inside an object-fit: contain box.
    const box = img.getBoundingClientRect();
    const nw = img.naturalWidth || box.width;
    const nh = img.naturalHeight || box.height;
    const fit = getComputedStyle(img).objectFit === 'cover' ? Math.max : Math.min;
    const scale = fit(box.width / nw, box.height / nh);
    const width = nw * scale;
    const height = nh * scale;
    return { left: box.left + (box.width - width) / 2, top: box.top + (box.height - height) / 2, width, height };
  };

  const moveLoupe = (trigger, event) => {
    const img = trigger.querySelector('img');
    if (!img) return;
    let lens = trigger.querySelector('.loupe-lens');
    if (!lens) {
      lens = document.createElement('span');
      lens.className = 'loupe-lens';
      lens.setAttribute('aria-hidden', 'true');
      lens.style.backgroundImage = `url("${trigger.dataset.loupeSrc || img.currentSrc || img.src}")`;
      trigger.append(lens);
    }
    const box = contentBox(img);
    const own = trigger.getBoundingClientRect();
    const x = Math.min(Math.max(event.clientX - box.left, 0), box.width);
    const y = Math.min(Math.max(event.clientY - box.top, 0), box.height);
    const size = lens.offsetWidth;
    lens.style.left = `${box.left - own.left + x}px`;
    lens.style.top = `${box.top - own.top + y}px`;
    lens.style.backgroundSize = `${box.width * LOUPE_ZOOM}px ${box.height * LOUPE_ZOOM}px`;
    lens.style.backgroundPosition = `${size / 2 - x * LOUPE_ZOOM}px ${size / 2 - y * LOUPE_ZOOM}px`;
    trigger.classList.add('is-loupe');
  };

  document.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse' || !loupeMedia.matches) return;
    const trigger = event.target.closest?.('[data-loupe]');
    if (trigger) moveLoupe(trigger, event);
  });

  document.addEventListener(
    'pointerout',
    (event) => {
      const trigger = event.target.closest?.('[data-loupe]');
      if (trigger && !trigger.contains(event.relatedTarget)) trigger.classList.remove('is-loupe');
    },
    true
  );

  /* ---------- Turn it in the hand (Item of the Week) ---------- */

  const initTilt = (root = document) => {
    root.querySelectorAll('[data-tilt]').forEach((card) => {
      if (card.dataset.tiltReady) return;
      card.dataset.tiltReady = 'true';
      const glare = document.createElement('span');
      glare.className = 'tilt-glare';
      glare.setAttribute('aria-hidden', 'true');
      card.append(glare);

      let current = { x: 0, y: 0 };
      let target = { x: 0, y: 0 };
      let frame = 0;
      const step = () => {
        current.x += (target.x - current.x) * 0.1;
        current.y += (target.y - current.y) * 0.1;
        card.style.transform = `perspective(900px) rotateX(${current.x.toFixed(2)}deg) rotateY(${current.y.toFixed(2)}deg)`;
        const settled = Math.abs(target.x - current.x) + Math.abs(target.y - current.y) < 0.02;
        frame = settled ? 0 : requestAnimationFrame(step);
        if (settled && target.x === 0 && target.y === 0) card.style.transform = '';
      };
      const kick = () => {
        frame ||= requestAnimationFrame(step);
      };

      card.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse' || reduceMotion.matches || !finePointer.matches) return;
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        target = { x: -y * 8, y: x * 10 };
        glare.style.setProperty('--glare-x', `${(x + 0.5) * 100}%`);
        glare.style.setProperty('--glare-y', `${(y + 0.5) * 100}%`);
        card.classList.add('is-tilting');
        kick();
      });
      card.addEventListener('pointerleave', () => {
        target = { x: 0, y: 0 };
        card.classList.remove('is-tilting');
        kick();
      });
    });
  };

  /* ---------- Gliding accordions ---------- */

  // Delegated, because the filter groups and the cart drawer are re-rendered.
  document.addEventListener('click', (event) => {
    const summary = event.target.closest?.('details[data-glide] > summary');
    if (!summary || reduceMotion.matches) return;
    const details = summary.parentElement;
    event.preventDefault();

    details._glide?.cancel();
    const start = details.offsetHeight;
    const closing = details.open;
    if (!closing) details.open = true;
    const end = closing ? summary.offsetHeight : details.scrollHeight;
    details.classList.toggle('is-closing', closing);
    details.style.overflow = 'hidden';

    const animation = details.animate([{ height: `${start}px` }, { height: `${end}px` }], {
      duration: closing ? 380 : 480,
      easing: closing ? 'cubic-bezier(0.7, 0, 0.2, 1)' : 'cubic-bezier(0.2, 0.7, 0.2, 1)',
    });
    details._glide = animation;
    animation.onfinish = () => {
      if (closing) details.open = false;
      details.classList.remove('is-closing');
      details.style.overflow = '';
      details._glide = null;
    };
    animation.oncancel = () => {
      details.classList.remove('is-closing');
      details.style.overflow = '';
    };
  });

  /* ---------- Into the bag ---------- */

  const MARK =
    '<svg viewBox="0 0 100 78.83" fill="currentColor" fill-rule="evenodd" aria-hidden="true"><path d="M26.42 0.1 49.69 0.31 49.9 22.33 49.37 24.74 43.29 28.72 41.4 29.35 18.03 29.66 18.24 30.71 21.38 34.91 47.69 67.51 48.32 67.51 50.52 65.3 82.6 30.08 50.84 29.56 50.31 26.73 50.31 4.72 50.84 0.21 76.21 0 98.01 18.45 100 20.86 99.79 22.54 95.6 27.36 48.11 78.83 43.4 74.11 0 22.33 0.73 20.75 3.67 18.87 6.6 15.93ZM29.66 7.97 15.3 19.39 43.08 19.71 44.65 18.13 45.07 16.88 44.86 9.33 44.34 7.97ZM56.92 9.01 56.39 19.81 57.13 20.75 86.06 20.75 86.79 20.23 74.11 9.01Z"/></svg>';

  const visible = (rect) => rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight;

  // Resolves once the mark has landed (or at once when there's nothing to show).
  const flyToBag = (from) =>
    new Promise((resolve) => {
      const bag = [...document.querySelectorAll('[data-cart-count]')]
        .map((count) => count.closest('a, button'))
        .find((link) => link && visible(link.getBoundingClientRect()));
      if (!from || !bag || reduceMotion.matches || !visible(from.getBoundingClientRect())) {
        resolve();
        return;
      }
      const a = from.getBoundingClientRect();
      const b = bag.getBoundingClientRect();
      const size = 24;
      const sx = a.left + a.width / 2 - size / 2;
      const sy = a.top + a.height / 2 - size / 2;
      const ex = b.left + b.width / 2 - size / 2;
      const ey = b.top + b.height / 2 - size / 2;
      const lift = Math.min(160, Math.max(70, Math.abs(sy - ey) * 0.35));

      const flyer = document.createElement('div');
      flyer.className = 'bag-flyer';
      flyer.innerHTML = MARK;
      document.body.append(flyer);

      const frames = [];
      for (let i = 0; i <= 14; i += 1) {
        const t = i / 14;
        const x = sx + (ex - sx) * t;
        const y = sy + (ey - sy) * t - lift * 4 * t * (1 - t);
        frames.push({
          transform: `translate(${x}px, ${y}px) scale(${1 - 0.4 * t}) rotate(${t * 360}deg)`,
          opacity: t > 0.94 ? 0 : 1,
        });
      }
      const flight = flyer.animate(frames, { duration: 760, easing: 'cubic-bezier(0.45, 0, 0.25, 1)' });
      const land = () => {
        flyer.remove();
        bag.classList.remove('is-bumped');
        void bag.offsetWidth;
        bag.classList.add('is-bumped');
        resolve();
      };
      flight.onfinish = land;
      flight.oncancel = land;
    });

  window.psjMotion = { flyToBag, reveal: initReveal };

  /* ---------- Boot ---------- */

  const boot = (root = document) => {
    initReveal(root);
    initTilt(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
